import { CompanyUser } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    QueryClient,
    useMutation,
    useQueryClient
} from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { isCompanyOwner } from '../util/company-roles';
import {
    FETCH_USER_COUNTS_QUERY_KEY,
    FETCH_USERS_PAGINATED_QUERY_KEY
} from '../queries/company-query-keys';

type UseDeleteCompanyUserActionProps = {
    user: CompanyUser;
    /** Joined owners in the company. The last one cannot be removed. */
    ownerCount: number;
};

type UseDeleteCompanyUserActionData = {
    /** Whether this viewer can remove this member right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    deleteUser: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Removing a member from the company: whether this viewer may do it, why not
 * when they may not, and the call itself. Kept together so a caller can wire up
 * a button — disabled state, tooltip, spinner and click — from one hook.
 */
export function useDeleteCompanyUserAction(
    props: UseDeleteCompanyUserActionProps
): UseDeleteCompanyUserActionData {
    const { user, ownerCount } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const canRemove = user.capabilities.canRemove;
    const [isAllowed, reason] = useMemo(() => {
        // The API decides who is permitted to remove this member, and hands
        // back a ready-to-show reason when they are not.
        if (!canRemove.allowed) {
            return [
                false,
                canRemove.reason ??
                    t('actions.delete_company_user.reason_not_permitted')
            ];
        }

        // Whether the removal is a valid move is still ours to work out.
        if (isCompanyOwner(user) && ownerCount <= 1) {
            return [false, t('actions.delete_company_user.reason_last_owner')];
        }

        return [true, ''];
    }, [canRemove, user, ownerCount, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `DELETE /v3/users/{id}`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'CANNOT_REMOVE_USER_WITH_OPEN_TASKS')) {
                // The one refusal the action flag can't predict — it only
                // shows up once the delete is attempted.
                return t('actions.delete_company_user.error_open_tasks');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.delete_company_user.error_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.users.deleteUser(user.id).then(() => {}),
        onSuccess: () => {
            deleteUserFromCache(queryClient, user.id);

            UIToast.success(t('actions.delete_company_user.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        deleteUser: () => mutation.mutateAsync().then(() => {}),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}

/**
 * The member is gone, so drop their own entry and let anything that counted or
 * listed them refetch.
 */
function deleteUserFromCache(
    queryClient: QueryClient,
    companyUserId: string
): void {
    queryClient.removeQueries({
        queryKey: ['fetchCompanyUser', companyUserId]
    });

    void queryClient.invalidateQueries({
        queryKey: [FETCH_USERS_PAGINATED_QUERY_KEY]
    });

    void queryClient.invalidateQueries({
        queryKey: [FETCH_USER_COUNTS_QUERY_KEY]
    });
}
